import { Helmet } from 'react-helmet-async'
import { useLocale, SUPPORTED_LOCALES } from '../../i18n/LocaleProvider'

interface PageMetaProps {
  title: string
  description?: string
  image?: string | null
  /** Path without the language query, e.g. /houses/aallokko. */
  path?: string
  noIndex?: boolean
}

const SITE_NAME = 'Lomasaaret Lakeside Cottages'
const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://lomasaaret.mnjlabs.com'

/**
 * Per-route metadata, including the hreflang set.
 *
 * The hreflang alternates are the part that earns its keep. A seven-language tourism site
 * without them competes against itself in search: Google indexes the Finnish and German
 * versions of the same cottage as duplicates and picks one. Declaring the alternates tells
 * it these are translations, and x-default names the version to show everyone else.
 */
export function PageMeta({ title, description, image, path = '', noIndex }: PageMetaProps) {
  const { locale } = useLocale()
  const fullTitle = path === '' ? title : `${title} · ${SITE_NAME}`
  const canonical = `${SITE_URL}${path}?lang=${locale}`

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />

      {SUPPORTED_LOCALES.map((option) => (
        <link
          key={option.code}
          rel="alternate"
          hrefLang={option.code}
          href={`${SITE_URL}${path}?lang=${option.code}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${path}`} />

      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={locale} />
      {image && <meta property="og:image" content={image} />}

      {noIndex && <meta name="robots" content="noindex,nofollow" />}
    </Helmet>
  )
}
